/**
 * Synced takes: recordings that know where in the song each moment was
 * sung, so the singing of one section can be played back on its own.
 *
 * A synced take is { chunks, frames, positions, lagFrames, hopSeconds,
 * sampleRate, quarterSeconds, songOffset }: its audio as chunks
 * ({ frame, samples }, stamped with the first sample's frame on the
 * recording's audio clock), a frame every hopSeconds ({ sample }) with its
 * place in the song (positions[i]: { pass, time } while the song was
 * playing, else null; time in the seconds the song's events were timed
 * at), and how far the voice trails that place (lagFrames: output and
 * input latency plus the singer's average lateness, measured from the
 * singing). Places are compared in quarter notes from the start of the
 * song (time / quarterSeconds + songOffset, where the section that was
 * playing starts), which stay put when the tempo changes.
 */

// The audio thread drops a few milliseconds now and then; gaps up to this
// are filled with the sound just before them, longer ones left silent
const LONGEST_FILL_SECONDS = 0.05;
// Crossfade where later singing takes over from earlier singing, and the
// fade in and out at the ends of a section
const CROSSFADE_SECONDS = 0.01;
const EDGE_FADE_SECONDS = 0.02;
// The lag is measured from pitch readings, which change about this long
// after the sound does (the pitch tracker's window), so the sound itself
// is this much earlier in the recording
const PITCH_READING_DELAY_SECONDS = 0.02;
// A section starts this much early, so the first consonant is not clipped
const LEAD_IN_SECONDS = 0.03;
// Positions are whole Transport ticks, so from frame to frame they step a
// tick more or less than the tempo gives; a step forward of more than this
// many times the tempo is a jump and starts a new stretch
const LONGEST_STEP = 3;
// Stretches at least this long are measured well enough to tell their
// tempo; those more than SAME_TEMPO off the take's tempo (sung before the
// tempo was changed) are left out
const MEASURED_SECONDS = 1;
const SAME_TEMPO = 0.02;

/**
 * Where the notes being played start in the song, in quarter notes. A
 * looping section's events are timed from the loop's start; each keeps its
 * time in the song as originalTime.
 */
export function sectionStart(events, looping, quarterSeconds) {
  const first = looping ? events[0] : null;
  const loopStart = first && Number.isFinite(first.originalTime) ? first.originalTime - first.time : 0;
  return loopStart / quarterSeconds;
}

// Unbroken stretches of a take (consecutive frames of one pass, moving on
// through the song) at the tempo most of it was sung at, with that tempo
// in quarter notes per sample; null if the song never played during it
function stretchesOf(take) {
  const { frames, positions, quarterSeconds, songOffset, sampleRate } = take;
  const hop = take.hopSeconds * sampleRate;
  const runs = [];
  let run = null;
  frames.forEach((frame, i) => {
    const position = positions[i];
    const q = position ? position.time / quarterSeconds + songOffset : null;
    if (run && position && position.pass === run.pass) {
      const ds = frame.sample - run.lastSample;
      const dq = q - run.lastQ;
      const span = run.lastSample - run.firstSample;
      const rate = span > 0 ? (run.lastQ - run.firstQ) / span : 0;
      if (ds > 0 && ds <= hop * 1.5 && dq >= 0 && (rate === 0 || dq <= LONGEST_STEP * rate * ds)) {
        run.lastSample = frame.sample;
        run.lastQ = q;
        run.count++;
        run.sumQ += q;
        run.sumSince += frame.sample - run.firstSample;
        return;
      }
    }
    if (run) runs.push(run);
    run = position ? { pass: position.pass, firstSample: frame.sample, firstQ: q, lastSample: frame.sample, lastQ: q, count: 1, sumQ: q, sumSince: 0 } : null;
  });
  if (run) runs.push(run);

  // The take's tempo: the rate most of its length ran at
  const measured = runs
    .filter(r => r.lastSample > r.firstSample && r.lastQ > r.firstQ)
    .map(r => ({ rate: (r.lastQ - r.firstQ) / (r.lastSample - r.firstSample), length: r.lastSample - r.firstSample }))
    .sort((a, b) => a.rate - b.rate);
  if (!measured.length) return null;
  const half = measured.reduce((sum, m) => sum + m.length, 0) / 2;
  let perSample = measured[measured.length - 1].rate;
  for (let i = 0, sum = 0; i < measured.length; i++) {
    sum += measured[i].length;
    if (sum >= half) {
      perSample = measured[i].rate;
      break;
    }
  }

  const stretches = runs.filter((r) => {
    const span = r.lastSample - r.firstSample;
    return span < MEASURED_SECONDS * sampleRate || Math.abs((r.lastQ - r.firstQ) / span / perSample - 1) <= SAME_TEMPO;
  });
  stretches.forEach((r) => {
    // Where it starts, fitted to all its frames rather than the first
    // frame's whole tick
    r.firstQ = (r.sumQ - perSample * r.sumSince) / r.count;
    r.length = r.lastSample - r.firstSample + Math.round(hop);
    r.to = r.firstQ + r.length * perSample;
  });
  return { perSample, stretches };
}

// The recording from frame `from`, `length` samples, into out[at...]
function readRecording(chunks, from, length, out, at, longestFill) {
  const end = from + length;
  let lo = 0;
  let hi = chunks.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (chunks[mid].frame + chunks[mid].samples.length <= from) lo = mid + 1;
    else hi = mid;
  }
  let pos = from;
  for (let c = lo; c < chunks.length && pos < end; c++) {
    const { frame, samples } = chunks[c];
    if (frame > pos) {
      const gapEnd = Math.min(frame, end);
      const gap = gapEnd - pos;
      const into = at + pos - from;
      if (gap <= longestFill && pos - gap >= from) out.copyWithin(into, into - gap, into);
      else out.fill(0, into, into + gap);
      pos = gapEnd;
      if (pos >= end) break;
    }
    const upTo = Math.min(end, frame + samples.length);
    if (upTo > pos) {
      out.set(samples.subarray(pos - frame, upTo - frame), at + pos - from);
      pos = upTo;
    }
  }
  if (pos < end) out.fill(0, at + pos - from, at + length);
}

/** Whether any of the take was sung between song quarters from and to. */
export function takeCovers(take, from, to) {
  const { positions, quarterSeconds, songOffset } = take;
  return positions.some(p => p && p.time / quarterSeconds + songOffset >= from && p.time / quarterSeconds + songOffset < to);
}

/**
 * The singing between song quarters `from` and `to` (and a moment before)
 * as a Float32Array at the take's sample rate, lasting as long as that
 * stretch did when it was sung. Where it was sung more than once (a loop,
 * singing it again) the latest singing wins; anything not sung is silent.
 * Null if none of it was sung.
 */
export function sectionAudio(take, sectionFrom, to) {
  const found = stretchesOf(take);
  if (!found) return null;
  const { perSample, stretches } = found;
  const { chunks, sampleRate } = take;
  const from = sectionFrom - LEAD_IN_SECONDS * sampleRate * perSample;
  const length = Math.round((to - from) / perSample);
  if (length <= 0) return null;
  const out = new Float32Array(length);
  const lag = Math.round((take.lagFrames * take.hopSeconds - PITCH_READING_DELAY_SECONDS) * sampleRate);
  const crossfade = Math.max(1, Math.round(CROSSFADE_SECONDS * sampleRate));
  const longestFill = Math.round(LONGEST_FILL_SECONDS * sampleRate);
  let sung = false;

  // In the order it was sung, so later singing replaces earlier
  stretches.forEach((r) => {
    const a = Math.max(r.firstQ, from);
    const b = Math.min(r.to, to);
    if (b <= a) return;
    const at = Math.round((a - from) / perSample);
    const count = Math.min(length - at, Math.round((b - a) / perSample));
    if (count <= 0) return;
    sung = true;
    // Crossfade with earlier singing only inside the section, not at its ends
    const edge = Math.min(crossfade, count >> 1);
    const headEdge = at > edge ? edge : 0;
    const tailEdge = at + count < length - edge ? edge : 0;
    const head = out.slice(at, at + headEdge);
    const tail = out.slice(at + count - tailEdge, at + count);
    readRecording(chunks, r.firstSample + lag + Math.round((a - r.firstQ) / perSample), count, out, at, longestFill);
    for (let j = 0; j < headEdge; j++) {
      const w = (j + 1) / (headEdge + 1);
      out[at + j] = head[j] * (1 - w) + out[at + j] * w;
    }
    for (let j = 0; j < tailEdge; j++) {
      const w = (j + 1) / (tailEdge + 1);
      const k = at + count - tailEdge + j;
      out[k] = tail[j] * w + out[k] * (1 - w);
    }
  });
  if (!sung) return null;

  const fade = Math.min(length >> 2, Math.round(EDGE_FADE_SECONDS * sampleRate));
  for (let i = 0; i < fade; i++) {
    out[i] *= i / fade;
    out[length - 1 - i] *= i / fade;
  }
  return out;
}
